import {
  createContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useCookie, useLocalStorage } from '@shared/hooks';
import { CookieProviderConfig } from '@shared/telemetry/cookies';

/**
 * Telemetry provider identifier
 */
export type ProviderId = 'analytics' | 'appMonitor';

/**
 * Configuration for storing consent state in cookies instead of localStorage
 * All options are optional and provided by the consuming app.
 */
export type ConsentCookieConfig = {
  readonly domain?: string;
  readonly path?: string;
  readonly maxAge?: number;
  readonly secure?: boolean;
  readonly sameSite?: 'Strict' | 'Lax' | 'None';
};

/**
 * Telemetry consent storage structure
 */
type TelemetryConsentState = {
  readonly analytics?: boolean;
  readonly appMonitor?: boolean;
};

/**
 * Privacy Context - manages consent state and cookie metadata
 */
export type PrivacyContextValue = {
  isCookieConsentGiven: boolean;
  isTelemetryConsentPromptHidden: boolean;

  cookies: ReadonlyArray<CookieProviderConfig>;
  registerCookies(config: CookieProviderConfig): void;
  getCookies(): ReadonlyArray<CookieProviderConfig>;
  getDisplayableCookies(): ReadonlyArray<CookieProviderConfig>;

  setCookieConsent(providerConsents: Record<ProviderId, boolean>): void;
  /**
   * Gets the current consent state from storage
   * Used for initializing UI state and by adapters to check current consent
   */
  getConsentState(): TelemetryConsentState;
};

export const PrivacyContextInner = createContext<
  PrivacyContextValue | undefined
>(undefined);

/**
 * Inner component - uses localStorage or cookies for consent persistence
 */
const PrivacyContextInitializer = ({
  children,
  disabled,
  consentCookieConfig,
  forceConsent,
}: PropsWithChildren<{
  disabled?: boolean;
  consentCookieConfig?: ConsentCookieConfig;
  forceConsent?: Record<ProviderId, boolean> | undefined;
}>) => {
  // Use cookies if config is provided, otherwise fallback to localStorage
  const [consentState, setConsentState] = consentCookieConfig
    ? useCookie<TelemetryConsentState>('pm:telemetry', {}, consentCookieConfig)
    : useLocalStorage<TelemetryConsentState>('pm:telemetry', {});

  // Override storage value with forceConsent when provided
  useEffect(() => {
    if (forceConsent) {
      setConsentState(forceConsent);
    }
  }, [forceConsent, setConsentState]);

  const [cookies, setCookies] = useState<CookieProviderConfig[]>([]);

  const contextValue: PrivacyContextValue = useMemo(
    () => ({
      isTelemetryConsentPromptHidden:
        disabled || Object.keys(consentState || {}).length > 0,

      isCookieConsentGiven:
        disabled ||
        consentState?.analytics === true ||
        consentState?.appMonitor === true ||
        false,

      cookies,

      registerCookies: (config: CookieProviderConfig) => {
        setCookies((prev) => {
          const exists = prev.some((c) => c.provider === config.provider);
          if (exists) {
            return prev;
          }
          return [...prev, config];
        });
      },

      getCookies: () => cookies,

      getDisplayableCookies: () =>
        cookies.filter((c) => c.displayInPrivacyOverlay === true),

      setCookieConsent: (providerConsents: Record<ProviderId, boolean>) => {
        const finalConsent = { ...providerConsents };

        // Force hidden providers (displayInPrivacyOverlay: false) to always be true
        cookies
          .filter((c) => c.displayInPrivacyOverlay === false)
          .forEach((c) => {
            finalConsent[c.provider] = true;
          });

        setConsentState(finalConsent);
      },

      getConsentState: () => consentState || {},
    }),
    [cookies, consentState],
  );

  return (
    <PrivacyContextInner.Provider value={contextValue}>
      {children}
    </PrivacyContextInner.Provider>
  );
};

export const PrivacyProvider = ({
  children,
  disabled,
  consentCookieConfig,
  forceConsent,
}: PropsWithChildren<{
  disabled?: boolean;
  consentCookieConfig?: ConsentCookieConfig;
  forceConsent?: Record<ProviderId, boolean> | undefined;
}>) => {
  return (
    <PrivacyContextInitializer
      disabled={disabled}
      consentCookieConfig={consentCookieConfig}
      forceConsent={forceConsent}
    >
      {children}
    </PrivacyContextInitializer>
  );
};
